import { useState } from 'react';
import type { FormEvent } from 'react';
import { Alert, Button, Card, Field, InstallPrompt, Input, Screen, Stack } from '../components';
import { useAuth } from '../auth/useAuth';

type Mode = 'signin' | 'signup';

export function AuthPage() {
  const { signIn, signUp } = useAuth();

  const [mode, setMode] = useState<Mode>('signin');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSignUp = mode === 'signup';

  function switchMode() {
    setMode(isSignUp ? 'signin' : 'signup');
    setError(null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (isSignUp && displayName.trim().length < 1) {
      setError('Enter the name the rest of the group will see.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);
    const result = isSignUp
      ? await signUp(email, password, displayName)
      : await signIn(email, password);
    setSubmitting(false);

    // On success the auth listener swaps the route out from under us, so there
    // is nothing to do here but surface failures.
    if (result.error) setError(result.error);
  }

  return (
    <Screen center maxWidth={420}>
      <Stack gap={6}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 'var(--fs-display)', letterSpacing: '-0.02em' }}>Tabi</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-body-lg)', marginTop: 'var(--sp-2)' }}>
            Split trip expenses with the group.
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit} noValidate>
            <Stack gap={4}>
              <h2>{isSignUp ? 'Create an account' : 'Sign in'}</h2>

              {error && <Alert tone="error">{error}</Alert>}

              {isSignUp && (
                <Field
                  label="Display name"
                  hint="How you'll appear to everyone else on the trip."
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      type="text"
                      autoComplete="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Sam"
                    />
                  )}
                </Field>
              )}

              <Field label="Email">
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                )}
              </Field>

              <Field label="Password" hint={isSignUp ? 'At least 6 characters.' : undefined}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    type="password"
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>

              <Button type="submit" size="lg" fullWidth loading={submitting}>
                {isSignUp ? 'Create account' : 'Sign in'}
              </Button>
            </Stack>
          </form>
        </Card>

        <InstallPrompt />

        <div style={{ textAlign: 'center', fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
          {isSignUp ? 'Already have an account?' : 'No account yet?'}{' '}
          <Button variant="ghost" size="sm" onClick={switchMode} type="button">
            {isSignUp ? 'Sign in' : 'Create one'}
          </Button>
        </div>
      </Stack>
    </Screen>
  );
}
